import React, { useEffect, useState } from 'react';
import type { ImportLine, XeroEmployee, XeroPayrollCalendar, XeroCalendarNextPeriod } from '../types';
import { calendarTypeLabel, employeePayrollCalendarId, employeePayrollCalendarName, employeePayrollCalendarType } from '../types';
import { payrunApi } from '../api/client';

interface ConfirmModalProps {
  lines: ImportLine[];
  reportMonth: string | null;
  reportYear: string | null;
  reportStartDate: string | null;
  reportEndDate: string | null;
  inferredCalendarType: 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY' | null;
  calendars: XeroPayrollCalendar[];
  employees: XeroEmployee[];
  onConfirm: (params: {
    payrollCalendarId: string;
    payPeriodStartDate: string;
    payPeriodEndDate: string;
    paymentDate: string;
  }) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

function fmt(n: number): string {
  return n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function defaultDates(
  reportMonth: string | null,
  reportYear: string | null,
  reportStartDate: string | null,
  reportEndDate: string | null,
) {
  function isoDate(y: number, m: number, d: number) {
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  function nextMonthSecond(isoEnd: string) {
    const [y, m] = isoEnd.split('-').map(Number);
    return m === 12 ? isoDate(y + 1, 1, 2) : isoDate(y, m + 1, 2);
  }
  function lastDayOfMonth(y: number, m: number) {
    // Day 0 of next month = last day of this month
    return new Date(Date.UTC(y, m, 0)).getUTCDate();
  }

  if (reportStartDate && reportEndDate) {
    return {
      start: reportStartDate,
      end: reportEndDate,
      payment: nextMonthSecond(reportEndDate),
    };
  }
  const now = new Date();
  const year = reportYear ? parseInt(reportYear) : now.getFullYear();
  const monthNames: Record<string, number> = {
    january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
    july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  };
  const month = reportMonth ? (monthNames[reportMonth.toLowerCase()] ?? now.getMonth() + 1) : now.getMonth() + 1;
  const endDay = lastDayOfMonth(year, month);
  const endIso = isoDate(year, month, endDay);
  return {
    start: isoDate(year, month, 1),
    end: endIso,
    payment: nextMonthSecond(endIso),
  };
}

function formatPeriod(period: { payPeriodStartDate: string; payPeriodEndDate: string }): string {
  function fmtOne(iso: string): string {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
    });
  }
  return `${fmtOne(period.payPeriodStartDate)} – ${fmtOne(period.payPeriodEndDate)}`;
}

function findMatchingCalendar(
  calendars: XeroPayrollCalendar[],
  inferredType: 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY' | null,
  lines: ImportLine[],
  employees: XeroEmployee[],
): string {
  const mappedCalendarIds = Array.from(new Set(
    lines
      .map((line) => employees.find((employee) => employee.EmployeeID === line.xeroEmployeeId))
      .map(employeePayrollCalendarId)
      .filter((id): id is string => !!id)
  ));
  if (mappedCalendarIds.length === 1 && calendars.some((calendar) => calendar.PayrollCalendarID === mappedCalendarIds[0])) {
    return mappedCalendarIds[0];
  }

  if (inferredType && calendars.length > 0) {
    const match = calendars.find((c) => c.CalendarType.toUpperCase() === inferredType);
    if (match) return match.PayrollCalendarID;
  }
  return calendars[0]?.PayrollCalendarID ?? '';
}

export default function ConfirmModal({
  lines,
  reportMonth,
  reportYear,
  reportStartDate,
  reportEndDate,
  inferredCalendarType,
  calendars,
  employees,
  onConfirm,
  onCancel,
  isSubmitting,
}: ConfirmModalProps) {
  const dates = defaultDates(reportMonth, reportYear, reportStartDate, reportEndDate);
  const [calendarId, setCalendarId] = useState(() => findMatchingCalendar(calendars, inferredCalendarType, lines, employees));
  const [startDate, setStartDate] = useState(dates.start);
  const [endDate, setEndDate] = useState(dates.end);
  const [paymentDate, setPaymentDate] = useState(dates.payment);

  // Proactively ask Xero what period it will actually assign the next draft
  // pay run for this calendar, so a mismatch can be caught here instead of
  // only after Xero has already created a wrong-period draft (POST /create
  // still re-verifies via GET as the authoritative last-line check).
  const [expectedPeriod, setExpectedPeriod] = useState<XeroCalendarNextPeriod | null>(null);
  const [expectedPeriodLoading, setExpectedPeriodLoading] = useState(false);
  const [expectedPeriodError, setExpectedPeriodError] = useState<string | null>(null);

  useEffect(() => {
    if (!calendarId) {
      setExpectedPeriod(null);
      return;
    }
    let cancelled = false;
    setExpectedPeriodLoading(true);
    setExpectedPeriodError(null);
    payrunApi.expectedPeriod(calendarId)
      .then(({ data }) => {
        if (!cancelled) setExpectedPeriod(data);
      })
      .catch(() => {
        if (!cancelled) {
          setExpectedPeriod(null);
          setExpectedPeriodError("Could not verify Xero's next pending period for this calendar — it will still be checked when you submit.");
        }
      })
      .finally(() => {
        if (!cancelled) setExpectedPeriodLoading(false);
      });
    return () => { cancelled = true; };
  }, [calendarId]);

  const periodMismatch =
    !!expectedPeriod &&
    (expectedPeriod.payPeriodStartDate !== startDate || expectedPeriod.payPeriodEndDate !== endDate);

  function applyExpectedPeriod() {
    if (!expectedPeriod) return;
    setStartDate(expectedPeriod.payPeriodStartDate);
    setEndDate(expectedPeriod.payPeriodEndDate);
  }

  const totalSuper = lines.reduce((sum, l) => sum + parseFloat(l.superAmount), 0);
  const paymentCount = new Set(
    lines.map((line) => line.xeroEmployeeId || `unmapped:${line.id}`),
  ).size;
  const monthLabel = reportMonth && reportYear ? `${reportMonth} ${reportYear}` : 'the selected period';
  const selectedCalendar = calendars.find((calendar) => calendar.PayrollCalendarID === calendarId);
  const calendarMismatches = lines
    .map((line) => {
      const employee = employees.find((item) => item.EmployeeID === line.xeroEmployeeId);
      return {
        line,
        employee,
        employeeCalendarId: employeePayrollCalendarId(employee),
        employeeCalendarType: employeePayrollCalendarType(employee),
        employeeCalendarName: employeePayrollCalendarName(employee),
      };
    })
    .filter((item) => !!item.line.xeroEmployeeId && item.employeeCalendarId !== calendarId);

  function handleSubmit() {
    if (!calendarId || calendarMismatches.length > 0 || periodMismatch) return;
    onConfirm({
      payrollCalendarId: calendarId,
      payPeriodStartDate: startDate,
      payPeriodEndDate: endDate,
      paymentDate,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="border-b border-gray-100 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100">
              <svg className="h-5 w-5 text-brand-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Create Xero Draft Pay Run</h2>
              <p className="text-sm text-gray-500">Review before continuing</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Notice */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            You are about to create a <strong>Draft</strong> Xero pay run for <strong>{monthLabel}</strong>.
            Super amounts will be applied to payslips.{' '}
            <strong>You must review and post the pay run manually in Xero.</strong>
          </div>

          {/* Summary */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 divide-y divide-gray-200">
            <div className="flex justify-between px-4 py-2.5 text-sm">
              <span className="text-gray-500">Super payments</span>
              <span className="font-medium text-gray-900">{paymentCount}</span>
            </div>
            <div className="flex justify-between px-4 py-2.5 text-sm">
              <span className="text-gray-500">Total super</span>
              <span className="font-semibold text-brand-700">${fmt(totalSuper)}</span>
            </div>
          </div>

          {/* Payroll calendar */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Payroll Calendar
            </label>
            {calendars.length > 0 ? (
              <select
                value={calendarId}
                onChange={(e) => setCalendarId(e.target.value)}
                className="input"
              >
                {calendars.map((c) => (
                  <option key={c.PayrollCalendarID} value={c.PayrollCalendarID}>
                    {c.Name} ({c.CalendarType})
                  </option>
                ))}
              </select>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                No payroll calendars found. Please create one in Xero Payroll settings.
              </div>
            )}
            {selectedCalendar && (
              <p className="mt-2 text-xs text-gray-500">
                This is a <span className="font-semibold text-gray-700">{calendarTypeLabel(selectedCalendar.CalendarType)}</span> payroll calendar.
                {' '}Only employees assigned to this calendar will be included in the pay run.
              </p>
            )}
          </div>

          {calendarMismatches.length > 0 && selectedCalendar && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
              <p className="font-semibold">Payroll calendar mismatch.</p>
              <p className="mt-1">
                The selected payroll calendar is {calendarTypeLabel(selectedCalendar.CalendarType)} ({selectedCalendar.Name}).
                Contractors on a different calendar will not be included in this pay run.
              </p>
              <ul className="mt-2 space-y-1">
                {calendarMismatches.map(({ line, employeeCalendarType, employeeCalendarName }) => (
                  <li key={line.id}>
                    {line.contractorName} → {calendarTypeLabel(employeeCalendarType)}
                    {employeeCalendarName ? ` (${employeeCalendarName})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Period start</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input text-xs" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Period end</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input text-xs" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Payment date</label>
              <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="input text-xs" />
            </div>
          </div>

          {expectedPeriodLoading && (
            <p className="text-xs text-gray-400">Checking Xero's next pending period for this calendar…</p>
          )}
          {expectedPeriodError && (
            <p className="text-xs text-amber-600">{expectedPeriodError}</p>
          )}
          {!expectedPeriodLoading && periodMismatch && expectedPeriod && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
              <p className="font-semibold">
                {expectedPeriod.source === 'blocking-draft'
                  ? 'An unposted draft pay run already exists for this calendar.'
                  : "These dates don't match Xero's next pending period."}
              </p>
              <p className="mt-1">
                {expectedPeriod.source === 'blocking-draft'
                  ? `Xero has an unposted draft for ${formatPeriod(expectedPeriod)} blocking this calendar. Open Xero Payroll and post or delete it, then try again.`
                  : `Xero processes payroll periods in strict order — the next period it will actually assign is ${formatPeriod(expectedPeriod)}, regardless of the dates set above.`}
              </p>
              {expectedPeriod.source !== 'blocking-draft' && (
                <button
                  type="button"
                  onClick={applyExpectedPeriod}
                  className="mt-2 rounded-md border border-amber-300 bg-white px-2.5 py-1 font-medium text-amber-800 hover:bg-amber-100"
                >
                  Use Xero's expected period ({formatPeriod(expectedPeriod)})
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 border-t border-gray-100 px-6 py-4">
          <button onClick={onCancel} disabled={isSubmitting} className="btn-secondary flex-1">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!calendarId || calendarMismatches.length > 0 || periodMismatch || isSubmitting}
            className="btn-primary flex-1"
          >
            {isSubmitting ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Creating pay run…
              </>
            ) : (
              'Confirm & Create'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
